import React, { useEffect } from 'react';
import { GetServerSideProps, NextPageWithLayout } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { IContentTypeEntries } from '@constants/metas/default';
import { getOrCreateLDCookiesSsr } from '@components/bucketTest/bucket.utils';
import CMSPage from '@components/organisms/content-stack/cms-page';
import { ContentStackPageNames } from '@enums/contentStack';
import { useAnalytics } from '@context/analytics/analytics.context';
import extractFromContext from '@helpers/utils/extract-from-context';
import ContentStackService from '@services/content-stack-service';
import logger from '@helpers/utils/logger/client';
import DefaultLayout from '@layouts/default-layout';
import { isRequest404Error, isRequestError } from '@helpers/errors';
import RequestError from '@errors/request-error';
import UserService from '@services/user-service';
import { User } from '@interfaces/models/user';
import { checkShouldShowBanner } from '@helpers/utils/general';
import useRestoreHpScrollPosition from '@hooks/cms/use-restore-hp-scroll-position';
import { getAppInitialProps } from '@helpers/initial-props';
// import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import getServerFeatureFlag from '@hooks/bucket-test/use-ssr-feature-flag';
import { useShoppingPreferences } from '@hooks/user/use-shopping-preferences';
import { ShoppingPreferenceModal } from '@components/user-preferences-modal/user-shopping-preferences-modal';
import { ShoppingPrefsPayload } from '@services/user-shopping-preferences-service';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { prefetchStickyNavData, extractStickyNavParamsFromSSR } from '@helpers/utils/sticky-nav/sticky-nav-prefetch';
import { shouldEvaluateStickyNavFF } from '@helpers/utils/sticky-nav/sticky-nav-utils';

const ZendeskWebWidget = dynamic(() => import('@components/molecules/zendesk-web-widget'), { ssr: false });

type HomePageProps = {
  contentTypeEntries: IContentTypeEntries;
  universe: '1' | '2' | null;
  shouldUpdatePrefs: boolean;
};

const HomePage: NextPageWithLayout<HomePageProps> = (props) => {
  const { contentTypeEntries, universe, shouldUpdatePrefs } = props;
  const { t: tA11y } = useTranslation('a11y');
  const shouldShowBanner = checkShouldShowBanner(contentTypeEntries);
  const { hpScrollPosition, scrollToCmsElementId } = useRestoreHpScrollPosition();
  const { preferences, source, isEnabled, hasUserSelectedPrefs, updatePreferences } = useShoppingPreferences();
  const { updateDataLayer } = useAnalytics();

  useEffect(() => {
    if (!!hpScrollPosition) {
      scrollToCmsElementId(hpScrollPosition);
    }
  }, []);

  useEffect(() => {
    updateDataLayer({
      user_shopping_preferences: `{ "universe": "${
        hasUserSelectedPrefs && !!preferences?.universe ? preferences?.universe : null
      }" }`,
    });
  }, [preferences?.universe, hasUserSelectedPrefs]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (isEnabled && shouldUpdatePrefs && universe) {
      // Debounce the update through URL to let the automatic update come first
      // this is a temporary fix until the backend send back the correct preferences in the response
      timeoutId = setTimeout(() => {
        updatePreferences({
          universe: parseInt(universe, 10),
          categories: preferences?.categories ?? [],
          custom: preferences?.custom ?? [],
        });
      }, 1000);
    }
    return () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, shouldUpdatePrefs, universe, preferences?.categories, preferences?.custom]);

  return (
    <DefaultLayout shouldShowBanner={shouldShowBanner}>
      <h1 className="visuallyhidden">{tA11y('A11Y.LOGO_HEADING')}</h1>
      <CMSPage {...{ contentTypeEntries, preferences, preferenceSource: source }} />
      <ZendeskWebWidget />
      {isEnabled && <ShoppingPreferenceModal />}
    </DefaultLayout>
  );
};

HomePage.Layout = [];
HomePage.PageType = 'HomePage';

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    const queryClient = new QueryClient();
    const initialProps = await getAppInitialProps(context, queryClient);

    const { preferences, locale, sessionId, userId, serverAxiosRequestData, userShoppingPreferences } =
      extractFromContext(context);
    const cmsFallback = context?.query?.cmsFallback === 'true';
    const { serverAnonymousId, randomRangeId } = getOrCreateLDCookiesSsr(context);
    const { getFeatureFlag } = getServerFeatureFlag(context, serverAnonymousId, randomRangeId);
    let serverAnonymousIdWithTimestamp: string = null;
    const universe = context?.query?.universe ?? userShoppingPreferences?.universe ?? null;
    const shouldUpdatePrefs = context?.query?.updatePrefs === '1';
    let shoppingPrefsHeader: ShoppingPrefsPayload | null = null;

    if (universe) {
      shoppingPrefsHeader = { universe: parseInt(universe as string, 10) };
    }

    try {
      await getFeatureFlag('test-server-side-flag-evaluation');
    } catch (error) {
      logger.error(error, 'Error fetching test-server-side-flag-evaluation feature flag');
    }

    if (sessionId && userId) {
      try {
        const MILLISECONDS_PER_SECOND = 1000;
        const user: User = await UserService.getUser(userId, serverAxiosRequestData);
        if (user?.inscriptionDate) {
          const inscriptionDateAsTimestampInSeconds =
            new Date(user.inscriptionDate).getTime() / MILLISECONDS_PER_SECOND;
          serverAnonymousIdWithTimestamp = `${serverAnonymousId};${inscriptionDateAsTimestampInSeconds}`;
        }
      } catch (e) {
        logger.error(`Error fetching user data: ${e}`);
      }
    }

    const apiRequests = [
      serverSideTranslations(locale, ['default-layout', 'vc-authentication', 'a11y', 'common', 'shopping-preferences']),
      ContentStackService.getDataForCMSPage(
        ContentStackPageNames.HOME_PAGE,
        {
          ...preferences,
          userId,
          serverAnonymousId: serverAnonymousIdWithTimestamp ?? serverAnonymousId,
          csBranch: context?.query?.csBranch as string,
          shoppingPreferences: shoppingPrefsHeader,
        },
        { cmsFallback },
      ),
    ];

    const promiseResults = await Promise.allSettled(apiRequests);

    const [translations, contentTypeEntries] = promiseResults.map((result, index) => {
      if (result.status === 'fulfilled') {
        return result.value;
      }

      if (!cmsFallback && index === 1) {
        throw new RequestError(result.reason);
      }

      return null;
    });

    if (!contentTypeEntries && cmsFallback) {
      return {
        redirect: {
          destination: '/new-items/',
          permanent: false,
        },
      };
    }

    if (shouldEvaluateStickyNavFF(extractStickyNavParamsFromSSR(context, initialProps))) {
      const isStickyNavEnabled = await getFeatureFlag('enable-multi-cat-sticky-entrypoint-nav');
      if (isStickyNavEnabled) {
        await prefetchStickyNavData(context, queryClient);
      }
    }

    return {
      props: {
        ...initialProps,
        ...translations,
        contentTypeEntries: contentTypeEntries || {},
        universe,
        shouldUpdatePrefs,
        dehydratedState: dehydrate(queryClient),
      },
    };
  } catch (err) {
    if (isRequestError(err)) {
      if (!isRequest404Error(err)) {
        logger.error(err, 'There was an API request error on the Home Page - getServerSideProps - index.page.tsx');
      }
      return {
        notFound: true,
      };
    }
    logger.error(err, 'The server failed to render the Home Page - getServerSideProps - index.page.tsx');
    throw err;
  }
};

export default HomePage;
